import { useState, useEffect, useRef, useCallback } from 'react'
import { getPublicLogs } from '@/api/public'
import type { RequestLog } from '@/api/logs'
import type { ConnectionStatus } from './useProjectLogs'
import { API_BASE_URL } from '@/lib/constants'

const MAX_LOGS = 200

interface UsePublicLogsReturn {
  logs: RequestLog[]
  isLoading: boolean
  connectionStatus: ConnectionStatus
  loadMore: () => Promise<void>
  clearLogs: () => void
  hasMore: boolean
}

export function usePublicLogs(slug: string, projectId: string): UsePublicLogsReturn {
  const [logs, setLogs] = useState<RequestLog[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('connecting')
  const [hasMore, setHasMore] = useState(false)

  // REST 初期ロード
  useEffect(() => {
    if (!slug) return
    let cancelled = false
    setIsLoading(true)
    setLogs([])
    getPublicLogs(slug, { limit: 50 })
      .then((data) => {
        if (cancelled) return
        setLogs(data)
        setHasMore(data.length === 50)
      })
      .catch(console.error)
      .finally(() => { if (!cancelled) setIsLoading(false) })
    return () => { cancelled = true }
  }, [slug])

  // WebSocket (トークンなし — public プロジェクトはサーバーが許可)
  useEffect(() => {
    if (!projectId) {
      setConnectionStatus('disconnected')
      return
    }

    const protocol = location.protocol === 'https:' ? 'wss:' : 'ws:'
    const wsHost = API_BASE_URL ? new URL(API_BASE_URL).host : location.host
    const url = `${protocol}//${wsHost}/ws/projects/${projectId}/logs`

    setConnectionStatus('connecting')
    const ws = new WebSocket(url)

    ws.addEventListener('open', () => setConnectionStatus('connected'))

    ws.addEventListener('message', (event) => {
      try {
        const log = JSON.parse(event.data as string) as RequestLog
        setLogs((prev) => {
          const next = [log, ...prev]
          return next.length > MAX_LOGS ? next.slice(0, MAX_LOGS) : next
        })
      } catch { /* ignore */ }
    })

    ws.addEventListener('close', () => setConnectionStatus('disconnected'))
    ws.addEventListener('error', () => setConnectionStatus('disconnected'))

    return () => { ws.close() }
  }, [projectId])

  const logsRef = useRef(logs)
  logsRef.current = logs

  const loadMore = useCallback(async () => {
    const oldest = logsRef.current[logsRef.current.length - 1]
    if (!oldest) return
    const data = await getPublicLogs(slug, { limit: 50, before: oldest.created_at })
    setLogs((prev) => {
      const combined = [...prev, ...data]
      return combined.length > MAX_LOGS ? combined.slice(0, MAX_LOGS) : combined
    })
    setHasMore(data.length === 50)
  }, [slug])

  const clearLogs = useCallback(() => { setLogs([]); setHasMore(false) }, [])

  return { logs, isLoading, connectionStatus, loadMore, clearLogs, hasMore }
}
